import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { UserManager } from 'oidc-client-ts';
import { jwtDecode } from 'jwt-decode';
import { appHeaders } from 'controls/appIdentity';
import { safeJsonParse } from '@rcw/utils';
import { oidcConfig } from '../authConfig';

// Single UserManager instance for the entire app lifetime
const userManager = new UserManager(oidcConfig);

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let renewalTimer = null;

    // ULA doesn't support OIDC silent renew via iframe — use Gateway's
    // RefreshUlaToken endpoint instead, which proxies the ULA refresh flow.
    // Returns the refreshed oidc-client-ts user, or null if renewal failed.
    const renewToken = async () => {
      try {
        const storageKey = `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`;
        const storedJson = localStorage.getItem(storageKey);
        const storedUser = safeJsonParse(storedJson);
        const refreshToken = storedUser?.refresh_token;
        if (!refreshToken) {
          console.error('[AuthContext] No refresh token available for renewal');
          return null;
        }
        const response = await fetch(
          `${process.env.REACT_APP_CUSTOMERS}/sso/RefreshUlaToken`,
          {
            method: 'POST',
            headers: { ...appHeaders, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              token: refreshToken,
              clientId: process.env.REACT_APP_CLIENT_ID,
            }),
          },
        );
        if (!response.ok) {
          console.error('[AuthContext] Token refresh failed:', response.status);
          return null;
        }
        const data = await response.json();
        const newIdToken = data.idToken;
        if (!newIdToken) {
          console.error('[AuthContext] No idToken in refresh response');
          return null;
        }
        // Decode new expiry from the JWT exp claim (seconds since epoch)
        const jwtPayload = jwtDecode(newIdToken);
        const newExpiresAt = jwtPayload.exp;
        // Patch localStorage so oidc-client-ts reads the updated tokens
        const updatedStored = { ...storedUser, id_token: newIdToken, expires_at: newExpiresAt };
        localStorage.setItem(storageKey, JSON.stringify(updatedStored));
        // Sync React state — getUser() reads from the store we just updated
        return await userManager.getUser();
      } catch (e) {
        console.error('[AuthContext] Auto-renewal failed:', e);
        return null;
      }
    };

    const scheduleRenewal = (oidcUser) => {
      if (renewalTimer) clearTimeout(renewalTimer);
      if (!oidcUser?.expires_at) return;
      // oidc-client-ts built-in timer requires a non-empty access_token,
      // but ULA returns access_token: "" — so we implement our own renewal timer.
      const expiresInMs = (oidcUser.expires_at - Math.floor(Date.now() / 1000)) * 1000;
      const renewInMs = Math.max(0, expiresInMs - 60000); // 60s before expiry
      renewalTimer = setTimeout(async () => {
        const refreshedUser = await renewToken();
        if (refreshedUser) {
          setUser(refreshedUser);
          scheduleRenewal(refreshedUser);
        } else {
          setUser(null);
        }
      }, renewInMs);
    };

    const onUserLoaded = (loadedUser) => {
      setUser(loadedUser);
      scheduleRenewal(loadedUser);
    };
    userManager.events.addUserLoaded(onUserLoaded);

    const initAuth = async () => {
      if (window.location.pathname.includes('/callback')) {
        try {
          const callbackUser = await userManager.signinRedirectCallback();
          setUser(callbackUser);
          const returnUrl = callbackUser.state?.returnUrl;
          window.location.replace(returnUrl || window.location.pathname.replace('/callback', ''));
        } catch (error) {
          console.error('[AuthContext] Callback error:', error);
          window.location.replace('/cloud/');
        }
      } else {
        let currentUser = await userManager.getUser();
        // A token restored from storage may have already expired while the tab
        // was closed — refresh it before exposing it, so consumers never see
        // isLoading:false paired with a stale token (which the Gateway 401s on).
        if (currentUser?.expired) {
          currentUser = await renewToken();
        }
        if (currentUser) {
          scheduleRenewal(currentUser);
        }
        setUser(currentUser);
      }
      setIsLoading(false);
    };

    initAuth();

    return () => {
      if (renewalTimer) clearTimeout(renewalTimer);
      userManager.events.removeUserLoaded(onUserLoaded);
    };
  }, []);

  const contextValue = useMemo(() => ({
    user,
    isLoading,
    isSigningOut,
    signinRedirect: (returnUrl) => userManager.signinRedirect({
      state: { returnUrl: returnUrl || window.location.pathname },
    }),
    signoutRedirect: async () => {
      // ULA returns empty access_token — use id_token as Bearer and refresh_token in body
      const storageKey = `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`;
      const storedJson = localStorage.getItem(storageKey);
      const storedUser = safeJsonParse(storedJson);
      const idToken = storedUser?.id_token;
      const refreshToken = storedUser?.refresh_token;

      // Immediately clear session and update UI
      setIsSigningOut(true);
      setUser(null);
      navigate('/', { replace: true });
      await userManager.removeUser();

      // Await signout before navigating — keepalive causes Chrome to process the
      // response after page unload, which triggers a file download when ULA responds
      // with Content-Disposition: attachment. Awaiting here keeps the response in
      // the current page context where the body is never read, so no download occurs.
      if (idToken || refreshToken) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
          await fetch(`${process.env.REACT_APP_AUTHORITY}oidc/signout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
            redirect: 'manual',
            signal: controller.signal,
          });
        } catch (e) {
          console.error('[AuthContext] Signout error:', e);
        } finally {
          clearTimeout(timeoutId);
        }
      }

      setIsSigningOut(false);
    },
  }), [user, isLoading, isSigningOut, navigate]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
